import React from 'react';
import { X } from 'lucide-react';
import EditDropdown from '../edit/EditDropdown';
import { ACCOUNT_TYPE_OPTIONS, DETAIL_TYPE_OPTIONS } from '../edit/constants';
import { GRID_TEMPLATE } from './constants';
import { isRowEmpty, type DraftField, type ValidatedRow } from '../../../../hooks/useCreateMultipleAccounts';

interface AccountRowEditorProps {
  row: ValidatedRow;
  index: number;
  showError: boolean;
  onUpdateCell: (idx: number, field: DraftField, value: string) => void;
  onPaste: (e: React.ClipboardEvent<HTMLInputElement>, startIdx: number, col: DraftField) => void;
  onDelete: (idx: number) => void;
}

export default function AccountRowEditor({ row, index, showError, onUpdateCell, onPaste, onDelete }: AccountRowEditorProps) {
  const isError = showError && row.status === 'error';
  const nameEmptyErr = isError && !row.name.trim();
  return (
    <div
      className={`grid gap-2.5 px-4 py-2 items-center text-[13px] ${index > 0 ? 'border-t border-lineSoft' : ''}`}
      style={{ gridTemplateColumns: GRID_TEMPLATE, borderLeft: isError ? '3px solid #8B1538' : 'none', paddingLeft: isError ? 13 : 16 }}
    >
      <div className="text-ink4 text-[11.5px] text-right" style={{ fontFamily: 'ui-monospace, monospace' }}>{index + 1}</div>
      <input
        type="text"
        value={row.number}
        onChange={(e) => onUpdateCell(index, 'number', e.target.value)}
        onPaste={(e) => onPaste(e, index, 'number')}
        className="w-full h-9 px-3 border border-line rounded-lg text-[13.5px] text-ink bg-white hover:border-ink4 focus:border-teal-dark focus:outline-none focus:ring-2 focus:ring-teal-dark/15"
        style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace' }}
      />
      <input
        type="text"
        value={row.name}
        onChange={(e) => onUpdateCell(index, 'name', e.target.value)}
        onPaste={(e) => onPaste(e, index, 'name')}
        placeholder={isRowEmpty(row) ? 'Account name' : ''}
        className="w-full h-9 px-3 border rounded-lg text-[13.5px] text-ink bg-white hover:border-ink4 focus:border-teal-dark focus:outline-none focus:ring-2 focus:ring-teal-dark/15"
        style={{ borderColor: nameEmptyErr ? '#8B1538' : '#D8DEE6', background: nameEmptyErr ? '#FFF8F8' : '#fff' }}
      />
      <EditDropdown value={row.accountType} onChange={(v) => onUpdateCell(index, 'accountType', v)} options={ACCOUNT_TYPE_OPTIONS} />
      <EditDropdown value={row.detailType} onChange={(v) => onUpdateCell(index, 'detailType', v)} options={DETAIL_TYPE_OPTIONS} />
      <button
        type="button"
        onClick={() => onDelete(index)}
        title="Delete row"
        aria-label="Delete row"
        className="w-8 h-8 rounded-md text-ink3 hover:text-[#8B1538] hover:bg-[#FCE8EE] inline-flex items-center justify-center bg-transparent border-none cursor-pointer"
      >
        <X className="w-4 h-4" />
      </button>
      {isError && row.errorMessage && (
        <div className="text-[11px] mt-1 font-medium" style={{ gridColumn: '3 / span 3', color: '#8B1538' }}>
          {row.errorMessage}
        </div>
      )}
    </div>
  );
}